/**
 * The cover a course card shows when the creator has not set a photo.
 *
 * Two Learnbee placeholders rather than one: grey-blue for a draft, purple for
 * a published course. The cards carry no Draft / Published chip any more (the
 * status tabs do that job), so the cover is where a glance down the grid still
 * tells the two apart.
 *
 * The two files are the artwork as supplied, scaled to 1280x800 — the card
 * aspect — so a cover is one cached request and no bigger than it needs to be.
 */
import type { CourseGroup } from '@/lib/db'

export const COURSE_PLACEHOLDER = {
  draft: '/thumbnails/course-draft.png',
  published: '/thumbnails/course-published.png',
} as const

type Coverable = { thumbnail_url?: string | null; share_code?: string | null }

/** Whatever this course should show as its cover. A blank string counts as unset. */
export function courseThumbnail(course: Coverable): string {
  return course.thumbnail_url?.trim()
    || (course.share_code ? COURSE_PLACEHOLDER.published : COURSE_PLACEHOLDER.draft)
}

/**
 * The cover for a translation family. The ROOT's photo is the family's photo,
 * but publishing is a family-wide fact (any language being live counts), so the
 * placeholder must be read from the whole family or a deck would look like a
 * draft while sitting under the Published tab.
 */
export function groupThumbnail(group: CourseGroup): string {
  return group.root.thumbnail_url?.trim()
    || (group.all.some(c => !!c.share_code) ? COURSE_PLACEHOLDER.published : COURSE_PLACEHOLDER.draft)
}
